import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { AGENT_AUDIO_TIME_FIELD } from 'hooks/transaction/useAgentAudioTimeTracker';
import { AGENT_TIME_FIELD } from 'hooks/transaction/useAgentTimeTracker';
import { PROCESS_TIME_FIELD } from 'utils/constants';
import { FENIX_EXPORTED_TO_YMS_FIELD } from 'utils/fenixYms';

/**
 * Which store actions mean "a field received an effective value", for `field.value.applied`
 * (BEAST-13036).
 *
 * NEITHER SEAM IS `setFieldValue` ALONE, and the ticket's single named seam
 * (`setFieldValueSource`) turned out to be neither necessary nor sufficient. What the code
 * actually does, verified on this branch:
 *
 * - `setFieldValueSource` has three dispatch sites and TWO of them are
 *   `restoreDiscardedFormValues` rehydrating a reopened transaction, which is a restore and not a
 *   new application. Only `copySheetAnnouncementHandler` is a real automation write.
 * - `personAutofill` / `automationSection` dispatch `setAutomationValues`, whose reducer calls
 *   `applyAutomationToFormValues` ONLY when `isSplitViewMode`. In tab view they write
 *   `form.automation`, which `Automation.jsx` renders as a display-only badge with no
 *   click-to-apply, and submit builds its payload from `form.values` alone
 *   (`formControlSagas/index.js:97`). So in tab view an automation suggestion is NOT an effective
 *   value and there is correctly nothing to report.
 * - `copyBisonAnnouncementHandler` and `copyConditionalsAutofillHandler` write through plain
 *   `setFieldValue` and record NO source at all, which is why an unrecorded source maps to
 *   automation with the source id `unknown` rather than to manual.
 *
 * Hence two rules, each matched to its seam:
 *
 *   DIRECT - the action payload names one field and the reducer writes unconditionally, so emit on
 *   the OCCURRENCE of the action. No memo: a repeated write IS a repeated application, and the only
 *   action that could double-report one write is deliberately not watched (see
 *   `directValueWriteActions`).
 *
 *   BULK - the action drives many fields through a reducer with guards this module must not try to
 *   mirror, and it can be re-run carrying fields it already applied. Emit on a CHANGE in the
 *   field's recorded source, tracked against a memo, so one applied value produces one event and a
 *   repeated pass over already-applied fields produces none.
 *
 * In both rules a field whose recorded source resolves to manual is NOT emitted here: it marks the
 * open field period dirty instead and reports once at blur, so ten keystrokes stay one event.
 */

/**
 * Bookkeeping form values that are not agent-facing fields and must not produce a value event.
 *
 * `agentTime` is a transaction column set once by `useAgentTimeTracker` on the first field focus,
 * explicitly out of scope for this ticket and not telemetry; `agentAudioTime` is its audio twin.
 * `sendToBison` is a control flag `copyBisonAnnouncementHandler` sets to `true` via `setFieldValue`
 * alongside the announcement fields - a toggle, not an agent-facing field value.
 * All are written with `setFieldValue`, so without this list they would each report as an
 * automation application on every transaction.
 */
export const VALUE_APPLIED_EXCLUDED_FIELDS = new Set([AGENT_TIME_FIELD, AGENT_AUDIO_TIME_FIELD, 'sendToBison']);

/**
 * Actions whose payload names exactly one field that the reducer writes unconditionally.
 *
 * DELIBERATELY NOT `setFieldValueSource`, and that omission is the most important thing in this
 * file. It has exactly two dispatch sites and neither needs watching: `restoreDiscardedFormValues`
 * (filtered out by `isRestore` anyway) and `copySheetAnnouncementHandler`, where it is always paired
 * with a `setFieldValue` for the same field. Watching both halves of that pair started two workers
 * that each waited a tick, read the same final source and both emitted - one applied value, two
 * events with different `event_id`s, which the backend's dedup-on-`event_id` cannot collapse.
 *
 * Watching only the value write fixes that at the root: `onDirectValueWrite`'s `yield delay(0)`
 * already lets the paired `setFieldValueSource` land before the source is read, so the source is
 * still correct. The alternative - a memo on this seam - traded the duplicate for a SILENT LOSS:
 * double-clicking a second sheet-announcement row re-applies the same fields with the same source,
 * and a memo keyed on (form, field, source) would have suppressed that real second application for
 * the lifetime of the form. In an immutable store a missing fact is worse than a repeated one, for
 * the same reason `lifecycle.js` refuses to smooth focus periods: the analytical layer can always
 * merge, never split.
 *
 * Resolved LAZILY, inside the saga, rather than at module evaluation: `formControlSlice`
 * transitively reaches `rootSaga`, so reading `formControlActions.<x>.type` at import time lands on
 * a partially initialized module and throws. Keep these as functions.
 */
export function directValueWriteActions() {
    return [
        // Plain programmatic write: the bison and conditionals autofill handlers, the sheet
        // announcement handler, and `blurHandler`'s formatting normalization all land here.
        formControlActions.setFieldValue.type,
        // The per-keystroke path. Resolves to manual, so it marks the period dirty and emits nothing.
        formControlActions.setFieldValueAndMarkEdited.type,
        // Marks an agent edit without writing a value; same manual deferral.
        formControlActions.markFieldUserEdited.type,
    ];
}

/**
 * Actions that drive many fields at once, all of which apply values ONLY in split view - which is
 * exactly when `applyAutomationToFormValues` runs. Lazy for the same reason as above.
 */
export function bulkAutomationActions() {
    return [
        formControlActions.setAutomationValues.type,
        formControlActions.updateTransactionDataAutomation.type,
        formControlActions.fetchTransactionFormConfigSuccess.type,
    ];
}

/**
 * The two timer-derived suggestion writes, each of which applies a value to ONE known field under
 * `AUTOMATION_SOURCE.AUTO`.
 *
 * These were left out at first because they fire repeatedly on a timer and their reducers carry
 * value-dedup guards that cannot be mirrored from the action payload, so reporting them on
 * occurrence would fill the stream with repeats of the same applied value. The source-change rule
 * the bulk seam uses solves exactly that - a repeat leaves `fieldValueSources[field]` on `'Auto'`,
 * so the memo suppresses it - which is why they are instrumented now, on that rule rather than on
 * occurrence. Unlike the bulk actions these apply values in BOTH modes, so there is no split-view
 * gate here.
 */
export function autoSuggestionActions() {
    return [formControlActions.setProcessTimeSuggestion.type, formControlActions.setExportedToYmsSuggestion.type];
}

/**
 * The single field each of those two actions can write. Resolved lazily for the same reason the
 * action lists are: dereferencing `formControlActions` at module scope lands on a partially
 * initialized module, because `formControlSlice` transitively reaches `rootSaga`.
 */
function autoSuggestionFieldFor(actionType) {
    if (actionType === formControlActions.setProcessTimeSuggestion.type) return PROCESS_TIME_FIELD;
    if (actionType === formControlActions.setExportedToYmsSuggestion.type) return FENIX_EXPORTED_TO_YMS_FIELD;
    return null;
}

/**
 * @returns {{ formId: (string|number), fieldName: string }|null} `null` when the action cannot have
 *   written a value at all - without `writeFieldValue` the reducer only updates the suggestion badge.
 */
export function readAutoSuggestionTarget(action) {
    if (!action?.payload?.writeFieldValue) return null;

    const formId = action.payload.formId;
    const fieldName = autoSuggestionFieldFor(action.type);
    if (formId === undefined || formId === null || !fieldName) return null;

    return { formId, fieldName };
}

/**
 * The two actions that ARE an agent edit: both stamp `fieldValueSources[field] = 'user'` in their
 * own reducer, so a watcher does not have to wait for a sibling action to learn the source.
 */
export function isUserEditAction(action) {
    return action?.type === formControlActions.setFieldValueAndMarkEdited.type || action?.type === formControlActions.markFieldUserEdited.type;
}

/** @returns {{ formId: (string|number), fieldName: string }|null} */
export function readDirectWrite(action) {
    // `restoreDiscardedFormValues` re-applies values that were already applied and reported once,
    // when a previously discarded transaction is reopened. It marks its dispatches `isRestore` so
    // that burst does not produce a second `field.value.applied` for every restored field.
    if (action?.payload?.isRestore) return null;

    const formId = action?.payload?.formId;
    const fieldName = action?.payload?.fieldName;
    if (formId === undefined || formId === null || typeof fieldName !== 'string' || !fieldName) return null;
    if (VALUE_APPLIED_EXCLUDED_FIELDS.has(fieldName)) return null;
    return { formId, fieldName };
}

/**
 * @returns {{ formId: (string|number) }|null} the form a bulk automation action applied values to,
 *   or `null` when the action cannot have written any (it was not a split-view pass).
 */
export function readBulkAutomationTarget(action) {
    // Every one of the three reducers guards its `applyAutomationToFormValues` call on this flag.
    if (!action?.payload?.isSplitViewMode) return null;

    const formId = action.payload.transactionId ?? action.payload.formId ?? action.payload.automation?.id;
    if (formId === undefined || formId === null) return null;

    return { formId };
}
